// ---------------------------------------------------------------------------
// folderTree — order a flat folder list into depth-first tree order.
//
// listFolders returns folders flat (each with parentFolderId + depth). To render
// a readable, indented folder picker we need parent-before-child ordering with a
// computed nesting depth. This does a stable DFS from the roots; siblings are
// sorted by name. It never drops a folder: any orphan (its parent isn't in the
// list — e.g. a folder the caller can see whose parent it can't) is appended at
// depth 0 rather than silently lost.
// ---------------------------------------------------------------------------

import type { FolderResponse } from '../api/vectrosApi';

export interface FolderTreeNode {
  readonly folder: FolderResponse;
  /** Nesting depth (0 = root) — used for indentation in the picker. */
  readonly depth: number;
}

/** Sentinel parent key for root folders (parentFolderId null/undefined). Not a
 *  valid folder id, so it can never collide with a real parent. */
const ROOT_KEY = ' root';

/**
 * Return `folders` in depth-first tree order with a computed display depth.
 * Siblings are ordered by name (then id, for stability). Cycle- and orphan-safe.
 */
export function orderFoldersAsTree(folders: ReadonlyArray<FolderResponse>): FolderTreeNode[] {
  const childrenByParent = new Map<string, FolderResponse[]>();
  for (const f of folders) {
    const key = f.parentFolderId ?? ROOT_KEY;
    const siblings = childrenByParent.get(key) ?? [];
    siblings.push(f);
    childrenByParent.set(key, siblings);
  }

  const bySortKey = (a: FolderResponse, b: FolderResponse): number =>
    (a.name ?? '').localeCompare(b.name ?? '') || (a.id ?? '').localeCompare(b.id ?? '');

  const out: FolderTreeNode[] = [];
  const visited = new Set<string>();

  const visit = (parentKey: string, depth: number): void => {
    const kids = [...(childrenByParent.get(parentKey) ?? [])].sort(bySortKey);
    for (const folder of kids) {
      if (!folder.id || visited.has(folder.id)) continue; // cycle / dup guard
      visited.add(folder.id);
      out.push({ folder, depth });
      visit(folder.id, depth + 1);
    }
  };
  visit(ROOT_KEY, 0);

  // Orphans: folders whose parent wasn't reachable from a root. Append at depth 0
  // so the picker still lists them.
  for (const folder of folders) {
    if (!folder.id || visited.has(folder.id)) continue;
    visited.add(folder.id);
    out.push({ folder, depth: 0 });
  }
  return out;
}
